/**
 * @file Cached item lookup and parent-board task date helpers.
 */

import { appData, appDataDetails } from '../../app/state/state.js';
import {
    getBoardCollectionNameForItemType,
    getBoardRegistryEntryByItemType,
    getBoardRegistryEntries,
} from '../board/boardRegistry.js';
import { soonestDate } from './dateUtils.js';
import { isItemClosed } from './closedState.js';

/**
 * Finds an item (patient, project, task, asset, user) by its ID within the global appData or detail view data.
 * @param {string | null} id - The ID of the item to find.
 * @returns {object | null} The found item object, or null if not found.
 */
export function findItem(id) {
    if (!id) {return null;}
    const users = appData?.users || [];
    const detailTasks = appDataDetails?.tasks || [];

    for (const board of getBoardRegistryEntries()) {
        const collectionItems = appData?.[board.collectionName] || [];
        const item = collectionItems.find(i => i.id === id);
        if (item) {return item;}
    }

    let item = detailTasks.find(i => i.id === id);
    if (item) {return item;}
    item = users.find(i => i.id === id);
    if (item) {return item;}

    // console.warn(`Item with ID ${id} not found.`); // Optional warning
    return null; // Return null explicitly if not found
}

/**
 * Determines the board/user type of an item from cached app data.
 * @param {string | null} id - The ID of the item.
 * @returns {string | null} The type name, or null if not found.
 */
export function getItemType(id) {
    if (!id) {return null;}
    for (const board of getBoardRegistryEntries()) {
        if (appData?.[board.collectionName]?.some(i => i.id === id)) {
            return board.itemType;
        }
    }
    if (appDataDetails?.tasks?.some(i => i.id === id)) {return 'task';}
    if (appData?.users?.some(i => i.id === id)) {return 'user';}
    return null;
}

/**
 * Resolves the primary display label for a cached item using registry metadata.
 * @param {object | null | undefined} item - Cached item data.
 * @param {string} [fallback=''] - Fallback label.
 * @returns {string} Display label or fallback.
 */
export function getItemDisplayName(item, fallback = '') {
    if (!item) {return fallback;}

    const itemType = item.id ? getItemType(item.id) : null;
    const titleField = itemType
        ? getBoardRegistryEntryByItemType(itemType)?.fields?.titleField
        : null;

    return (
        (titleField ? item[titleField] : null) ||
        item.name ||
        item.title ||
        item.item ||
        item.patient_name ||
        fallback
    );
}

/**
 * Converts an item type to its collection name.
 * Parent-board types resolve through the board registry; legacy and
 * non-board callers keep the previous special-case and plural fallback.
 * @param {string | null} itemType - The singular item type (e.g., 'task', 'patient').
 * @returns {string | null} The plural collection name (e.g., 'tasks', 'patients'), or null if input is null.
 */
export function getCollectionName(itemType) {
    if (!itemType) {return null;}
    if (itemType === 'drug_inventory') {return 'drug_inventory';} // Special case: non-plural collection name
    const boardCollectionName = getBoardCollectionNameForItemType(itemType);
    if (boardCollectionName) {return boardCollectionName;}
    return itemType.endsWith('s') ? itemType : `${itemType}s`;
}

/**
 * Finds the earliest due date among all *open* tasks linked to a specific parent item.
 * @param {string | null} parentId - The ID of the parent item (patient, project, asset).
 * @param {Array<object>} taskList - The list of tasks to search (e.g., appData.tasks or appDataDetails.tasks).
 * @returns {string | null} The earliest due date string (YYYY-MM-DD or format from DB), or null if no open tasks with dates exist.
 */
export function getNextActionDate(parentId, taskList) {
    if (!parentId) {return null;}
    const tasks = Array.isArray(taskList) ? taskList : (appData?.tasks || []); // Use provided list or global list

    const openTasksWithDates = tasks
        .filter(t => t.parentId === parentId && t.due_date && !isItemClosed(t))
        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date)); // Sort earliest first

    return openTasksWithDates.length > 0 ? openTasksWithDates[0].due_date : null;
}

/**
 * Determines the primary date to use for sorting an item (Patient, Project, Asset) in Kanban views.
 * It prioritizes the earliest due date of any open linked tasks, then falls back to item-specific dates.
 * @param {object | null} item - The patient, project, or asset object.
 * @param {Array<object>} taskList - The list of all tasks to search within (e.g., appData.tasks or appDataDetails.tasks).
 * @returns {string | null} The relevant date string (YYYY-MM-DD or YYYY-MM-DDTHH:mm format from DB), or null.
 */
export function getKanbanSortDate(item, taskList) {
    if (!item || !item.id) {return null;}
    const itemType = getItemType(item.id); // Uses getItemType from this file
    if (!itemType) {return null;}

    const allRelevantTasks = Array.isArray(taskList) ? taskList : []; // Ensure taskList is an array

    // Filter for open tasks associated with the item that have a due date
    const openTasksWithDates = allRelevantTasks
        .filter(t => t.parentId === item.id && t.due_date && !isItemClosed(t))
        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date)); // Sort earliest first

    // If open tasks with dates exist, return the earliest one
    if (openTasksWithDates.length > 0) {
        return openTasksWithDates[0].due_date;
    }

    // Fallback to item-specific dates if no relevant open tasks found
    switch (itemType) {
        case 'patient': return item.next_appointment || null;
        case 'project': return item.delivery_date || null; // Project's own deadline
        case 'asset': return item.next_service_due || null; // Asset's maintenance date
        default: return null;
    }
}

/**
 * Gets the category configuration array for a given item type from `appData.config`,
 * sorted by the 'order' property (ascending).
 * @param {'patient' | 'project' | 'asset' | 'lab' | 'task' | 'maintenance'} type - The type of item.
 * @returns {Array<object>} A sorted array of category configuration objects, or an empty array if not found/invalid.
 */
export function getSortedCategories(type) {
    let configKey;
    switch (type) {
        case 'patient': configKey = 'patientCategoryConfig'; break;
        case 'project': configKey = 'projectCategoryConfig'; break;
        case 'asset': configKey = 'assetCategoryConfig'; break;
        case 'lab': configKey = 'labCategoryConfig'; break;
        case 'task': configKey = 'taskCategoryConfig'; break;
        case 'maintenance': configKey = 'maintenanceCategoryConfig'; break;
        default: return [];
    }

    const categories = appData?.config?.[configKey];
    if (!Array.isArray(categories)) {
        return []; // Return empty array if config key doesn't exist or isn't an array
    }

    // Create a copy and sort it by the 'order' property (ascending)
    // Handle potentially missing 'order' property by defaulting to 0
    return [...categories].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
}

/**
 * Finds the next open task associated with a parent item, sorted by due date (earliest first).
 * @param {string | null} parentId - The ID of the parent item.
 * @returns {object | null} The task object for the next open task, or null if none exist or parentId is invalid.
 */
export function getNextOpenTask(parentId, taskList = appData?.tasks || []) {
    if (!parentId) {return null;}
    const tasks = Array.isArray(taskList) ? taskList : (appData?.tasks || []);

    const openTasksWithDates = tasks
        .filter(t => t.parentId === parentId && t.due_date && !isItemClosed(t))
        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date)); // Earliest first

    return openTasksWithDates.length > 0 ? openTasksWithDates[0] : null;
}

/**
 * Calculates the primary display/sort date for an Asset Kanban card when grouped by date.
 * Considers asset category ('issue' category has no date), next service due, and earliest open task due date.
 * @param {object | null} asset - The asset object.
 * @returns {string | null} The date string (YYYY-MM-DD or format from DB) to use for sorting/display, or null if no relevant date.
 */
export function getAssetDisplayDate(asset, taskList = appData?.tasks || []) {
    if (!asset || !asset.id) {return null;}

    const equipmentStatuses = appData?.config?.equipmentStatus || [];
    const assetCategories = appData?.config?.assetCategoryConfig || [];

    const status = equipmentStatuses.find(s => s.id === asset.equipment_status) || {};
    const category = assetCategories.find(c => c.id === status.category) || {};

    // Assets in the 'issue' category don't have a relevant date for date-based sorting/grouping
    if (category.id === 'issue') {
        return null;
    }

    // Find the earliest open task linked to this asset
    const nextOpenTask = getNextOpenTask(asset.id, taskList);
    const serviceDateStr = asset.next_service_due || null;
    const taskDateStr = nextOpenTask ? nextOpenTask.due_date : null;

    // Return the soonest valid date between the next service and the next task
    return soonestDate(taskDateStr, serviceDateStr); // Uses function from this file
}

/**
 * Finds the earliest due date among all *open* tasks linked to a specific Project.
 * @param {string | null} projectId - The ID of the project item.
 * @param {Array<object>} taskList - The list of tasks to search (usually appData.tasks).
 * @returns {string | null} The earliest due date string (YYYY-MM-DD or format from DB), or null if no open tasks with dates exist or projectId is invalid.
 */
export function getNextDueDate(projectId, taskList) {
    if (!projectId) {return null;}
    // Use provided taskList if it's a valid array, otherwise default to global tasks
    const tasks = Array.isArray(taskList) ? taskList : (appData?.tasks || []);

    const openTasksWithDates = tasks
        .filter(t => t.parentId === projectId && t.due_date && !isItemClosed(t))
        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date)); // Sort earliest first

    return openTasksWithDates.length > 0 ? openTasksWithDates[0].due_date : null;
}
